import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import apiClient from '../../services/apiClient';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Page = styled.div`
  padding: 24px 30px;
  min-height: 100vh;
  background: #f8f9fa;
`;

const Title = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  color: #e9af66;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const Stat = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 18px 20px;
  border: 1px solid #f0e6d6;
`;

const StatValue = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  color: #e9af66;
`;

const StatLabel = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 4px;
`;

const Tabs = styled.div`
  display: inline-flex;
  background: #fff;
  border: 1px solid #f0e6d6;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? '#e9af66' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#b8823f')};
  padding: 10px 22px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;

  th, td {
    text-align: left;
    padding: 12px 8px;
    border-bottom: 1px solid #f3f4f6;
  }

  th {
    color: #9ca3af;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
  }
`;

const Empty = styled.p`
  font-family: 'Poppins', sans-serif;
  color: #6b7280;
  text-align: center;
  padding: 32px;
`;

const Pager = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  justify-content: flex-end;
  align-items: center;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;

  button {
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 8px;
    padding: 6px 12px;
    cursor: pointer;
  }

  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const formatViewerName = (row) =>
  row.viewer?.name ||
  row.viewer?.shop_name ||
  row.viewerName ||
  row.viewerId?.name ||
  (row.viewerType === 'guest' ? 'Guest visitor' : row.viewerType || 'Guest');

const formatViewerMobile = (row) =>
  row.viewer?.mobile_number || row.viewerMobile || row.viewerId?.mobile_number || '—';

const VendorPhoneViews = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('visitors');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ totalViews: 0, uniqueViewers: 0, viewsToday: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  const loadStats = async () => {
    try {
      const res = await apiClient.get('/vendor/profile/views');
      setStats(res.data?.data || { totalViews: 0, uniqueViewers: 0, viewsToday: 0 });
    } catch {
      // keep zeros if stats endpoint is unavailable
    }
  };

  const load = async (page = 1, nextTab = tab) => {
    try {
      setLoading(true);
      const endpoint = nextTab === 'contacts'
        ? '/vendor/phone-views'
        : '/vendor/profile/viewers';
      const res = await apiClient.get(endpoint, { params: { page, limit: 20 } });
      setRows(res.data?.data || []);
      setPagination(res.data?.pagination || { page: 1, totalPages: 1, total: 0 });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load visitor data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    load(1, 'visitors');
  }, []);

  const switchTab = (nextTab) => {
    setTab(nextTab);
    load(1, nextTab);
  };

  return (
    <Page>
      <Title>{t('vendorPhoneViews.insightsTitle', 'Visitor Insights')}</Title>
      <Subtitle>
        {t(
          'vendorPhoneViews.insightsSubtitle',
          'See who visited your profile, how many times it was viewed, and which parties revealed your contact number.'
        )}
      </Subtitle>

      <StatsGrid>
        <Stat>
          <StatValue>{stats.totalViews || 0}</StatValue>
          <StatLabel>{t('vendorPhoneViews.totalViews', 'Total profile views')}</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{stats.uniqueViewers || 0}</StatValue>
          <StatLabel>{t('vendorPhoneViews.uniqueVisitors', 'Unique visitors')}</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{stats.viewsToday || 0}</StatValue>
          <StatLabel>{t('vendorPhoneViews.viewsToday', 'Views today')}</StatLabel>
        </Stat>
      </StatsGrid>

      <Tabs>
        <Tab type="button" $active={tab === 'visitors'} onClick={() => switchTab('visitors')}>
          {t('vendorPhoneViews.profileVisitors', 'Profile visitors')}
        </Tab>
        <Tab type="button" $active={tab === 'contacts'} onClick={() => switchTab('contacts')}>
          {t('vendorPhoneViews.contactLog', 'Contact log')}
        </Tab>
      </Tabs>

      <Card>
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <Empty>{error}</Empty>
        ) : rows.length === 0 ? (
          <Empty>
            {tab === 'contacts'
              ? t('vendorPhoneViews.empty', 'No phone views yet.')
              : t('vendorPhoneViews.emptyVisitors', 'No profile visitors yet.')}
          </Empty>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>{t('vendorPhoneViews.party', 'Party')}</th>
                  <th>{t('vendorPhoneViews.mobile', 'Mobile')}</th>
                  <th>
                    {tab === 'contacts'
                      ? t('vendorPhoneViews.source', 'Source')
                      : t('vendorPhoneViews.viewerType', 'Type')}
                  </th>
                  <th>{t('vendorPhoneViews.date', 'Date')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row._id}>
                    <td>{formatViewerName(row)}</td>
                    <td>{formatViewerMobile(row)}</td>
                    <td>{tab === 'contacts' ? (row.source || 'profile') : (row.viewerType || 'guest')}</td>
                    <td>{row.createdAt ? format(new Date(row.createdAt), 'dd MMM yyyy, HH:mm') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {pagination.totalPages > 1 && (
              <Pager>
                <button
                  type="button"
                  disabled={pagination.page <= 1}
                  onClick={() => load(pagination.page - 1)}
                >
                  Prev
                </button>
                <span>
                  {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => load(pagination.page + 1)}
                >
                  Next
                </button>
              </Pager>
            )}
          </>
        )}
      </Card>
    </Page>
  );
};

export default VendorPhoneViews;
